/**
 * TopBar — chrome that lives above the main content.
 *
 * Slots, left → right:
 *   1. GlobalSearch (the existing palette/search)
 *   2. Theme toggle (moved here from the sidebar)
 *   3. Notifications bell (already inboxed; same component)
 *   4. Profile menu — avatar + popover with Edit-profile drawer
 *      (name + change-password) and Sign out.
 *
 * The visual style is intentionally distinct from agent shells we've
 * shipped so far: a thin bar with a soft gradient backdrop, glassy
 * action chips, and a subtle accent ring on the active control. The
 * profile avatar is a gradient pill that doubles as a breadcrumb
 * back to the operator's identity.
 */
import { useEffect, useRef, useState, type JSX, type Ref } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, Edit3, KeyRound, LogOut, Menu, ShieldCheck, UserCircle2,
} from 'lucide-react';
import { GlobalSearch } from '../GlobalSearch';
import { NotificationsBell } from './NotificationsBell';
import { ThemeToggle } from '../../theme/theme-toggle';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { useSession } from '../../session';
import { usePermissions } from '../../lib/permissions';
import { AccessNotice } from '../ui/AccessNotice';
import {
  AdminApiClient, 
  type StaffRow,
} from '../../api';
import { userMessageFor } from '../../lib/api-state';

export interface TopBarProps {
  /** Narrow-layout navigation drawer wiring (see AppShell). */
  menuButtonRef?: Ref<HTMLButtonElement>;
  navOpen?: boolean;
  navId?: string;
  onToggleNav?: () => void;
}

export function TopBar({ menuButtonRef, navOpen = false, navId, onToggleNav }: TopBarProps = {}): JSX.Element {
  const { state, signOut } = useSession();
  const user = state.user;
  const [menuOpen, setMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the popover on outside click. Drawer states are managed
  // independently and stay open through this listener.
  useEffect(() => {
    if (!menuOpen) return undefined;
    function onDoc(e: MouseEvent): void {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  return (
    <div
      className="topbar"
      style={{
        position: 'sticky', top: 8, zIndex: 30,
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px',
        marginBottom: 12,
        borderRadius: 14,
        background:
          'linear-gradient(135deg, rgba(99, 102, 241, 0.10), rgba(167, 139, 250, 0.06) 50%, rgba(34, 211, 238, 0.06))',
        backdropFilter: 'blur(8px)',
        border: '1px solid var(--border)',
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.08)',
      }}
    >
      {onToggleNav && (
        <button
          ref={menuButtonRef}
          type="button"
          className="topbar__menu"
          aria-label="Navigation menu"
          aria-expanded={navOpen}
          aria-controls={navId}
          onClick={onToggleNav}
        >
          <Menu width={18} height={18} aria-hidden="true" />
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <GlobalSearch />
      </div>

      <ActionChip title="Theme">
        <ThemeToggle />
      </ActionChip>

      <ActionChip title="Notifications">
        <NotificationsBell />
      </ActionChip>

      <div ref={menuRef} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 10px 4px 4px',
            borderRadius: 999,
            border: '1px solid var(--border)',
            background:
              'linear-gradient(135deg, rgba(99, 102, 241, 0.18), rgba(167, 139, 250, 0.10))',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            transition: 'transform 120ms ease, box-shadow 120ms ease',
            boxShadow: menuOpen
              ? '0 0 0 2px rgba(167, 139, 250, 0.40)'
              : '0 1px 0 rgba(255, 255, 255, 0.04) inset',
          }}
        >
          <Avatar user={user} />
          <span className="topbar__name" style={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1 }}>
            {user?.email.split('@')[0] ?? 'Profile'}
          </span>
          <ChevronDown
            width={12} height={12}
            style={{
              transition: 'transform 160ms ease',
              transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              color: 'var(--text-muted)',
            }}
          />
        </button>

        {menuOpen && user && (
          <div
            role="menu"
            style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              minWidth: 240,
              padding: 6,
              borderRadius: 12,
              background: 'var(--bg-raised)',
              border: '1px solid var(--border)',
              boxShadow: '0 16px 40px rgba(0, 0, 0, 0.30)',
              display: 'flex', flexDirection: 'column', gap: 2,
            }}
          >
            <div
              style={{
                padding: '10px 10px 8px',
                display: 'flex', alignItems: 'center', gap: 10,
                borderBottom: '1px solid var(--border)',
                marginBottom: 4,
              }}
            >
              <Avatar user={user} size={36} />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '0.8125rem', fontWeight: 600,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {user.email.split('@')[0]}
                </div>
                <div
                  style={{
                    fontSize: '0.6875rem', color: 'var(--text-muted)',
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <UserCircle2 width={11} height={11} />
                  {user.role}
                  {user.mfa_ok && (
                    <>
                      <span aria-hidden>·</span>
                      <ShieldCheck width={11} height={11} color="#34d399" />
                      MFA
                    </>
                  )}
                </div>
              </div>
            </div>

            <MenuItem
              icon={Edit3}
              label="Edit profile"
              hint="Change your display name + password"
              onClick={() => { setMenuOpen(false); setEditOpen(true); }}
            />
            <MenuItem
              icon={LogOut}
              label="Sign out"
              tone="danger"
              onClick={() => { setMenuOpen(false); signOut(); }}
            />
          </div>
        )}
      </div>

      <ProfileDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />
    </div>
  );
}

function Avatar({ user, size = 28 }: {
  user: { email: string } | null | undefined;
  size?: number;
}): JSX.Element {
  const initial = (user?.email ?? '?').charAt(0).toUpperCase();
  return (
    <span
      aria-hidden
      style={{
        width: size, height: size,
        flexShrink: 0,
        borderRadius: 999,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size >= 36 ? '0.875rem' : '0.75rem',
        fontWeight: 700,
        color: '#0f172a',
        background:
          'linear-gradient(135deg, #c4b5fd, #93c5fd 60%, #a7f3d0)',
        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.55)',
      }}
    >
      {initial}
    </span>
  );
}

function ActionChip({ title, children }: { title: string; children: JSX.Element }): JSX.Element {
  return (
    <div
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: 4,
        borderRadius: 10,
        border: '1px solid var(--border)',
        background: 'var(--bg-overlay)',
        transition: 'border-color 120ms ease',
      }}
    >
      {children}
    </div>
  );
}

function MenuItem({
  icon: Icon, label, hint, tone = 'default', onClick,
}: {
  icon: typeof Edit3;
  label: string;
  hint?: string;
  tone?: 'default' | 'danger';
  onClick: () => void;
}): JSX.Element {
  const color = tone === 'danger' ? 'var(--danger)' : 'var(--text-primary)';
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px',
        borderRadius: 8,
        background: 'transparent',
        border: 'none',
        color,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
      }}
      onMouseEnter={(e) => { (e.currentTarget.style.background = 'var(--bg-overlay)'); }}
      onMouseLeave={(e) => { (e.currentTarget.style.background = 'transparent'); }}
    >
      <Icon width={14} height={14} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{label}</span>
        {hint && <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{hint}</span>}
      </span>
    </button>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Profile edit drawer — name + password change in one drawer.
 * ────────────────────────────────────────────────────────────────── */

function ProfileDrawer({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const { state } = useSession();
  const perms = usePermissions();
  // /internal/staff/me (read and write) is owner/admin only on the server.
  const canEditProfile = perms.canWrite;
  const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
  const [me, setMe] = useState<StaffRow | null>(null);
  const [name, setName] = useState('');
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [pwdMsg, setPwdMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Reload `me` whenever the drawer opens so the input shows the
  // latest server-side value (in case another tab edited it).
  useEffect(() => {
    if (!open) return;
    setProfileMsg(null);
    setPwdMsg(null);
    setOldPwd(''); setNewPwd(''); setConfirmPwd('');
    if (!canEditProfile) return;
    api.getMe().then((row) => {
      setMe(row);
      setName(row.name);
    }).catch((e) => {
      setProfileMsg({ kind: 'err', text: userMessageFor(e) });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submitProfile = async (): Promise<void> => {
    if (!name.trim() || name.trim() === me?.name) return;
    setSavingProfile(true); setProfileMsg(null);
    try {
      await api.updateMe({ name: name.trim() });
      setProfileMsg({ kind: 'ok', text: 'Saved.' });
      // Refresh local me-state so the input + avatar reflect the save.
      const fresh = await api.getMe().catch(() => null);
      if (fresh) setMe(fresh);
    } catch (e) {
      setProfileMsg({ kind: 'err', text: userMessageFor(e) });
    } finally {
      setSavingProfile(false);
    }
  };

  const submitPassword = async (): Promise<void> => {
    if (!oldPwd || !newPwd) return;
    if (newPwd !== confirmPwd) {
      setPwdMsg({ kind: 'err', text: "New password and confirmation don't match." });
      return;
    }
    setSavingPwd(true); setPwdMsg(null);
    try {
      await api.changePassword({ current_password: oldPwd, new_password: newPwd });
      setPwdMsg({ kind: 'ok', text: 'Password updated.' });
      setOldPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (e) {
      setPwdMsg({ kind: 'err', text: userMessageFor(e) });
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="md"
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <UserCircle2 width={14} height={14} />
          Edit profile
        </span>
      }
      subtitle={me ? `Signed in as ${me.email}` : undefined}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <AccessNotice reason={canEditProfile ? null : 'Your display name can be changed by Owner and Admin accounts. Your email and role are shown below.'} />
        <Section title="Identity">
          <Field label="Display name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={me?.email}
              disabled={!canEditProfile}
              style={inputStyle}
            />
          </Field>
          <Field label="Email" hint="Read-only — managed by your SSO provider.">
            <input value={me?.email ?? state.user?.email ?? ''} disabled style={{ ...inputStyle, opacity: 0.7 }} />
          </Field>
          <Field label="Role" hint="Only an owner can promote / demote.">
            <input value={me?.role ?? state.user?.role ?? ''} disabled style={{ ...inputStyle, opacity: 0.7 }} />
          </Field>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="default" size="sm"
              onClick={() => void submitProfile()}
              disabled={!canEditProfile || savingProfile || !name.trim() || name.trim() === me?.name}
            >
              {savingProfile ? 'Saving…' : 'Save name'}
            </Button>
            {profileMsg && (
              <span
                role={profileMsg.kind === 'err' ? 'alert' : 'status'}
                style={{
                  fontSize: '0.75rem',
                  color: profileMsg.kind === 'ok' ? 'var(--success)' : 'var(--danger)',
                }}
              >
                {profileMsg.text}
              </span>
            )}
          </div>
        </Section>

        <Section title="Change password" hint="Min 12 chars. Old password required.">
          <Field label="Current password">
            <input
              type="password"
              value={oldPwd}
              onChange={(e) => setOldPwd(e.target.value)}
              autoComplete="current-password"
              style={inputStyle}
            />
          </Field>
          <Field label="New password">
            <input
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              autoComplete="new-password"
              style={inputStyle}
            />
          </Field>
          <Field label="Confirm new password">
            <input
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              autoComplete="new-password"
              style={inputStyle}
            />
          </Field>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="default" size="sm"
              onClick={() => void submitPassword()}
              disabled={savingPwd || !oldPwd || !newPwd || newPwd !== confirmPwd}
            >
              <KeyRound width={12} height={12} />
              {savingPwd ? 'Updating…' : 'Update password'}
            </Button>
            {pwdMsg && (
              <span
                style={{
                  fontSize: '0.75rem',
                  color: pwdMsg.kind === 'ok' ? 'var(--success)' : 'var(--danger)',
                }}
              >
                {pwdMsg.text}
              </span>
            )}
          </div>
        </Section>
      </div>
    </Drawer>
  );
}

function Section({
  title, hint, children,
}: { title: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h3
        style={{
          margin: 0, marginBottom: 8,
          fontSize: '0.6875rem', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: 'var(--text-muted)',
        }}
      >
        {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
      {hint && (
        <p style={{ margin: '6px 0 0', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
          {hint}
        </p>
      )}
    </section>
  );
}

function Field({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: 8,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg-overlay)',
  color: 'var(--text-primary)',
  fontSize: '0.8125rem',
  outline: 'none',
  fontFamily: 'inherit',
} as const;
